import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React, { useMemo } from 'react';
import { Route, Routes, Navigate, generatePath } from 'react-router-dom';

import { DashboardPage } from 'src/pages/dashboard-page/dashboard-page';
import { DrillingCarpetPage } from 'src/pages/drilling-carpet-page/drilling-carpet-page';
import { DrillingPlanChartPage } from 'src/pages/drilling-plan-chart';
import { ImportConflictsPage } from 'src/pages/import-conflicts-page/import-conflicts-page';
import { ImportMatchingPage } from 'src/pages/import-matching-page/import-matching-page';
import { NewWellPage } from 'src/pages/new-well-page/new-well-page';
import { SettingsPage } from 'src/pages/settings-page';
import { WellPadPreparation } from 'src/pages/well-pad-preparation/well-pad-preparation';
import { WellsPage } from 'src/pages/wells';
import { ROUTES } from 'src/shared/constants/routes';
import { useStore } from 'src/store';
import {
  GOPLAN_DASHBOARD_ACCESS,
  GOPLAN_DRILLING_CARPET_GRAPH_EDIT,
  GOPLAN_DRILLING_CARPET_GRAPH_READ,
  GOPLAN_DRILLING_CARPET_PREPARATION_ACCESS_EDIT,
  GOPLAN_DRILLING_CARPET_PREPARATION_ACCESS_READ,
  GOPLAN_DRILLING_CARPET_WELL_CLUSTER_ACCESS_READ,
  GOPLAN_DRILLING_CARPET_WELLS_ACCESS_EDIT,
  GOPLAN_DRILLING_CARPET_WELLS_ACCESS_READ,
} from 'src/store/roles-store/roles';

export const Router: FC = observer(function Router() {
  const { rolesStore } = useStore();

  const indexPage = useMemo(() => {
    if (
      rolesStore.hasAccess([
        GOPLAN_DRILLING_CARPET_GRAPH_READ,
        GOPLAN_DRILLING_CARPET_GRAPH_EDIT,
        GOPLAN_DRILLING_CARPET_WELL_CLUSTER_ACCESS_READ,
      ])
    ) {
      return ROUTES.schedule;
    }

    if (rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_WELLS_ACCESS_READ, GOPLAN_DRILLING_CARPET_WELLS_ACCESS_EDIT])) {
      return ROUTES.wells;
    }

    if (
      rolesStore.hasAccess([
        GOPLAN_DRILLING_CARPET_PREPARATION_ACCESS_READ,
        GOPLAN_DRILLING_CARPET_PREPARATION_ACCESS_EDIT,
      ])
    ) {
      return ROUTES.wellPadPreparation;
    }

    if (rolesStore.hasAccess([GOPLAN_DASHBOARD_ACCESS])) {
      return ROUTES.dashboard;
    }

    return ROUTES.carpet;
  }, [rolesStore]);

  return (
    <Routes>
      <Route path={ROUTES.carpet} element={<DrillingCarpetPage />}>
        {rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_WELLS_ACCESS_READ, GOPLAN_DRILLING_CARPET_WELLS_ACCESS_EDIT]) && (
          <Route path={ROUTES.wells} element={<WellsPage />} />
        )}
        {rolesStore.hasAccess([
          GOPLAN_DRILLING_CARPET_PREPARATION_ACCESS_READ,
          GOPLAN_DRILLING_CARPET_PREPARATION_ACCESS_EDIT,
        ]) && <Route path={ROUTES.wellPadPreparation} element={<WellPadPreparation />} />}
        {rolesStore.hasAccess([
          GOPLAN_DRILLING_CARPET_GRAPH_READ,
          GOPLAN_DRILLING_CARPET_GRAPH_EDIT,
          GOPLAN_DRILLING_CARPET_WELL_CLUSTER_ACCESS_READ,
        ]) && <Route path={ROUTES.schedule} element={<DrillingPlanChartPage />} />}
        <Route path={ROUTES.matching} element={<ImportMatchingPage />} />
        <Route path={ROUTES.conflicts} element={<ImportConflictsPage />} />
        <Route path={ROUTES.newWell} element={<NewWellPage />} />
        <Route index element={<Navigate to={indexPage} />} />
      </Route>
      {rolesStore.hasAccess([GOPLAN_DASHBOARD_ACCESS]) && <Route path={ROUTES.dashboard} element={<DashboardPage />} />}
      <Route path={ROUTES.settings} element={<SettingsPage />}>
        <Route path={ROUTES['settings/section']}>
          <Route path={ROUTES['settings/section/subsection']}>
            <Route path="*" />
          </Route>
        </Route>
        <Route index element={<Navigate to={generatePath(ROUTES['settings/section'], { section: 'palette' })} />} />
      </Route>
      <Route path="*" element={<Navigate to={ROUTES.carpet} />} />
    </Routes>
  );
});

export default Router;
